import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import type { FeedId } from '@assistmd/types';
import { DashboardLayout } from '@/layouts/DashboardLayout';
import { LoginPage } from '@/pages/LoginPage';
import { VisitsPage } from '@/pages/VisitsPage';
import { VisitDetailPage } from '@/pages/VisitDetailPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { PrivacyPolicyPage } from '@/pages/PrivacyPolicyPage';
import { useAuthStore } from '@/lib/stores/auth-store';
import { getDashboardEnv } from '@/lib/env';
import { connect, disconnect } from '@/lib/ws/ws-client';

// Lazy-loaded pages — split into separate chunks
const TelemetryPage = lazy(() => import('@/pages/TelemetryPage').then(m => ({ default: m.TelemetryPage })));
const SettingsPage = lazy(() => import('@/pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const ReplayPage = lazy(() => import('@/pages/ReplayPage').then(m => ({ default: m.ReplayPage })));
const JobDetailPage = lazy(() => import('@/pages/JobDetailPage').then(m => ({ default: m.JobDetailPage })));
const PromptsPage = lazy(() => import('@/pages/ops/PromptsPage').then(m => ({ default: m.PromptsPage })));
const WorkflowBuilderPage = lazy(() => import('@/pages/ops/WorkflowBuilderPage').then(m => ({ default: m.WorkflowBuilderPage })));
const WorkflowSandboxPage = lazy(() => import('@/pages/ops/WorkflowSandboxPage').then(m => ({ default: m.WorkflowSandboxPage })));
const RunConsolePage = lazy(() => import('@/pages/ops/RunConsolePage').then(m => ({ default: m.RunConsolePage })));
const BindingsEditorPage = lazy(() => import('@/pages/ops/BindingsEditorPage').then(m => ({ default: m.BindingsEditorPage })));
const SmartFillPage = lazy(() => import('@/pages/ops/SmartFillPage').then(m => ({ default: m.SmartFillPage })));
const RateLimitsPage = lazy(() => import('@/pages/ops/RateLimitsPage').then(m => ({ default: m.RateLimitsPage })));
const DocumentsPage = lazy(() => import('@/pages/ops/DocumentsPage').then(m => ({ default: m.DocumentsPage })));
const QuotaPage = lazy(() => import('@/pages/ops/QuotaPage').then(m => ({ default: m.QuotaPage })));

const DASHBOARD_FEEDS: FeedId[] = ['A', 'C', 'D', 'E'];

function WsLifecycleBridge(): null {
  const initialized = useAuthStore((s) => s.initialized);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const doctorId = useAuthStore((s) => s.doctorId);
  const clinicId = useAuthStore((s) => s.clinicId);
  const wsUrl = getDashboardEnv().wsUrl;

  useEffect(() => {
    if (!initialized || !isAuthenticated) {
      disconnect();
      return;
    }

    connect({
      url: wsUrl,
      doctorId: doctorId ?? 'doctor-001',
      clinicId: clinicId ?? undefined,
      subscribeFeeds: DASHBOARD_FEEDS,
    });

    return () => {
      disconnect();
    };
  }, [clinicId, doctorId, initialized, isAuthenticated, wsUrl]);

  return null;
}

function PageLoader(): JSX.Element {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-pulse text-slate-400 text-sm">Loading...</div>
    </div>
  );
}

export function App(): JSX.Element {
  return (
    <BrowserRouter>
      <WsLifecycleBridge />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />

        <Route element={<DashboardLayout />}>
          {/* Eager — hot path */}
          <Route index element={<Navigate to="/visits" replace />} />
          <Route path="visits" element={<VisitsPage />} />
          <Route path="visits/:id" element={<VisitDetailPage />} />

          {/* Lazy — secondary pages */}
          <Route path="visits/:id/replay" element={<Suspense fallback={<PageLoader />}><ReplayPage /></Suspense>} />
          <Route path="jobs/:jobId" element={<Suspense fallback={<PageLoader />}><JobDetailPage /></Suspense>} />
          <Route path="telemetry" element={<Suspense fallback={<PageLoader />}><TelemetryPage /></Suspense>} />
          <Route path="settings" element={<Suspense fallback={<PageLoader />}><SettingsPage /></Suspense>} />

          {/* Lazy — ops pages */}
          <Route path="ops/prompts" element={<Suspense fallback={<PageLoader />}><PromptsPage /></Suspense>} />
          <Route path="ops/workflow-builder" element={<Suspense fallback={<PageLoader />}><WorkflowBuilderPage /></Suspense>} />
          <Route path="ops/workflow-sandbox" element={<Suspense fallback={<PageLoader />}><WorkflowSandboxPage /></Suspense>} />
          <Route path="ops/run-console" element={<Suspense fallback={<PageLoader />}><RunConsolePage /></Suspense>} />
          <Route path="ops/bindings" element={<Suspense fallback={<PageLoader />}><BindingsEditorPage /></Suspense>} />
          <Route path="ops/smartfill" element={<Suspense fallback={<PageLoader />}><SmartFillPage /></Suspense>} />
          <Route path="ops/rate-limits" element={<Suspense fallback={<PageLoader />}><RateLimitsPage /></Suspense>} />
          <Route path="ops/documents" element={<Suspense fallback={<PageLoader />}><DocumentsPage /></Suspense>} />
          <Route path="ops/quota" element={<Suspense fallback={<PageLoader />}><QuotaPage /></Suspense>} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
}
